import { useState, useRef, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import "./RassdLanding.css";
import "./mobile-menu.css";
import "./lang-switcher.css";
import emailjs from "@emailjs/browser";
import { translations } from "./rassdTranslations";

const ICONS = [
  <img src="/images/rss.svg" alt="RSS" width={28} height={28} />,
  <img src="/images/articles.svg" alt="Articles" width={28} height={28} />,
  <img src="/images/social.svg" alt="Social" width={28} height={28} />,
  <img src="/images/translation.svg" alt="Translation" width={28} height={28} />,
  <img src="/images/metadata.svg" alt="Metadata" width={28} height={28} />,
  <img src="/images/hot.svg" alt="Hot topics" width={28} height={28} />,
  <img src="/images/analytics.svg" alt="Analytics" width={28} height={28} />,
  <img src="/images/alarm.svg" alt="Alerts" width={28} height={28} />,
];

const LANG_OPTIONS = [
  { code: "en", flag: "EN" },
  { code: "ar", flag: "AR" },
];

function LangSwitcher({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);


  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = LANG_OPTIONS.find((o) => o.code === lang);

  return (
    <div
      ref={ref}
      className={`lang-switcher ${open ? "lang-switcher--open" : ""}`}
    >
      <button
        className="lang-switcher__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="lang-switcher__flag">{current.flag}</span>

        <svg
          className="lang-switcher__chevron"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="lang-switcher__dropdown" role="listbox">
          {LANG_OPTIONS.map((opt) => (
            <button
              key={opt.code}
              role="option"
              aria-selected={lang === opt.code}
              className={`lang-switcher__option ${lang === opt.code ? "lang-switcher__option--active" : ""
                }`}
              onClick={() => {
                setLang(opt.code);
                setOpen(false);
              }}
            >
              <span className="lang-switcher__flag">{opt.flag}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactForm({ t }) {
  const EMPTY = { firstName: "", lastName: "", email: "", subject: "", message: "" };
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const SERVICE_ID = "service_cr5xgel";
  const TEMPLATE_SOCIETE = "template_w5qbpgh";
  const TEMPLATE_CLIENT = "template_tnpngfd";
  const PUBLIC_KEY = "L_gNh2dKV1E56eJf9";

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = t.errors.firstName;
    if (!form.lastName.trim()) e.lastName = t.errors.lastName;
    if (!form.email.trim()) e.email = t.errors.email;
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = t.errors.emailInvalid;
    if (!form.subject.trim()) e.subject = t.errors.subject;
    if (!form.message.trim()) e.message = t.errors.message;
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setLoading(true);
    try {
      const templateData = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        subject: form.subject,
        message: form.message,
      };
      await emailjs.send(SERVICE_ID, TEMPLATE_SOCIETE, templateData, PUBLIC_KEY);
      await emailjs.send(SERVICE_ID, TEMPLATE_CLIENT, templateData, PUBLIC_KEY);
      setSubmitted(true);
    } catch (err) {
      alert(t.errorAlert);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="success-box">
        <div className="success-box__emoji">✅</div>
        <h3 className="success-box__title">{t.successTitle}</h3>
        <p className="success-box__desc">{t.successDesc}</p>
        <button
          className="btn-primary"
          style={{ marginTop: "20px", padding: "10px 24px" }}
          onClick={() => { setSubmitted(false); setForm(EMPTY); }}
        >
          {t.sendAnother}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">{t.firstName}</label>
          <input name="firstName" value={form.firstName} onChange={handleChange}
            className={`form-input ${errors.firstName ? "form-input--error" : ""}`}
            placeholder={t.firstName} />
          {errors.firstName && <span className="form-error">{errors.firstName}</span>}
        </div>
        <div className="form-group">
          <label className="form-label">{t.lastName}</label>
          <input name="lastName" value={form.lastName} onChange={handleChange}
            className={`form-input ${errors.lastName ? "form-input--error" : ""}`}
            placeholder={t.lastName} />
          {errors.lastName && <span className="form-error">{errors.lastName}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">{t.email}</label>
          <input name="email" type="email" value={form.email} onChange={handleChange}
            className={`form-input ${errors.email ? "form-input--error" : ""}`}
            placeholder={t.email} />
          {errors.email && <span className="form-error">{errors.email}</span>}
        </div>
        <div className="form-group">
          <label className="form-label">{t.subject}</label>
          <input name="subject" value={form.subject} onChange={handleChange}
            className={`form-input ${errors.subject ? "form-input--error" : ""}`}
            placeholder={t.subject} />
          {errors.subject && <span className="form-error">{errors.subject}</span>}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t.message}</label>
        <textarea name="message" value={form.message} onChange={handleChange}
          className={`form-textarea ${errors.message ? "form-textarea--error" : ""}`}
          placeholder={t.message} />
        {errors.message && <span className="form-error">{errors.message}</span>}
      </div>

      <button className="form-submit" onClick={handleSubmit} disabled={loading}>
        {loading ? t.sending : t.submit}
      </button>
    </div>
  );
}

export default function RassdLanding() {
  const getLangFromUrl = () => {
    const segment = window.location.pathname.split("/")[1];
    return segment === "ar" ? "ar" : "en";
  };

  const [lang, setLang] = useState(getLangFromUrl);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSetLang = (newLang) => {
    const currentPath = window.location.pathname;
    // Replace the first segment (/en or /ar) or prepend if missing
    const newPath = currentPath.replace(/^\/(en|ar)(\/|$)/, `/${newLang}$2`) || `/${newLang}`;
    const hasLangPrefix = /^\/(en|ar)(\/|$)/.test(currentPath);
    window.history.pushState(null, "", hasLangPrefix ? newPath : `/${newLang}${currentPath}`);
    setLang(newLang);
  };

  const t = translations[lang];
  const dir = lang === "ar" ? "rtl" : "ltr";
  const fontClass = lang === "ar" ? "rassd-root--ar" : "";
  const loginUrl = `https://rassd.app/${lang}/login`;

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const navIds = ["features", "why-newsroom", "contact-us"];

  const handleNav = (id) => {
    scrollTo(id);
    setMenuOpen(false);
  };

  return <>
    <Helmet>
      {/* 1. Définir la langue globale du document */}
      <html lang={lang} />
      <title>{t.meta.title}</title>
      <meta name="description" content={t.meta.description} />
      <link rel="alternate" href={`https://rassd.ai/ar/`} hreflang="ar" />
      <link rel="alternate" href={`https://rassd.ai`} hreflang="en" />
      <link rel="alternate" href={`https://rassd.ai`} hreflang="x-default" />
      <link rel="canonical" href={lang === 'ar' ? `https://rassd.ai/ar/` : 'https://rassd.ai'} />
      <meta property="og:title" content={t.meta.title} />
      <meta property="og:description" content={t.meta.description} />
      <meta property="og:locale" content={lang === 'ar' ? 'ar_AR' : 'en_US'} />
    </Helmet>


    <div className={`rassd-root ${fontClass}`} dir={dir} lang={lang}>

      <nav className="rassd-nav">
        <a href="/" className="rassd-nav__logo">
          <img src={lang === "ar" ? "/images/logo-ar.png" : "/images/logo.png"} alt="logo" width={130} />
        </a>

        <div className="rassd-nav__links">
          {t.nav.links.map((label, i) => (
            <button
              key={label}
              className="rassd-nav__link"
              onClick={() => scrollTo(navIds[i])}
            >
              {label}
            </button>
          ))}
          <LangSwitcher lang={lang} setLang={handleSetLang} />
          <button className="btn-primary">
            <a href={loginUrl}>{t.nav.login}</a>
          </button>
        </div>
        <div className="rassd-nav__right-mobile">
          <LangSwitcher lang={lang} setLang={handleSetLang} />
          <button
            className={`rassd-nav__hamburger ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>
        </div>

        <div className={`rassd-nav__mobile-menu ${menuOpen ? "open" : ""}`}>
          {t.nav.links.map((label, i) => (
            <button
              key={label}
              className="rassd-nav__mobile-link"
              onClick={() => handleNav(navIds[i])}
            >
              {label}
            </button>
          ))}
          <div className="rassd-nav__mobile-divider" />
          <button className="rassd-nav__mobile-login">
            <a href={loginUrl}>{t.nav.login}</a>
          </button>
        </div>
      </nav>

      <section className="rassd-hero">
        <h1 className="rassd-hero__title">{t.hero.title}</h1>
        <p className="rassd-hero__subtitle">{t.hero.subtitle}</p>
        <div className="rassd-hero__buttons">
          <button className="btn-outline">{t.hero.viewDemo}</button>
          <button className="btn-primary" onClick={() => scrollTo("contact-us")}>
            {t.hero.getStarted}
          </button>
        </div>
      </section>

      <section className="rassd-mockup">
        <img src="/images/mockup.png" alt="mockup" className="rassd-mockup-image" />
      </section>

      <section id="features" className="rassd-features">
        <h2 className="rassd-section__title">{t.features.sectionTitle}</h2>
        <div className="rassd-features__grid">
          {t.features.items.map((f, i) => (
            <div key={f.title} className="rassd-feature-card">
              <div className="rassd-feature-card__icon">{ICONS[i]}</div>
              <div className="rassd-feature-card__title">{f.title}</div>
              <div className="rassd-feature-card__desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>


      <section id="why-newsroom" className="rassd-why">
        <div className="rassd-why__grid">
          <div>
            <h2 className="rassd-why__title">{t.why.title}</h2>
            <p className="rassd-why__subtitle">{t.why.subtitle}</p>
          </div>
          <div>
            {t.why.items.map((item) => (
              <div key={item.num} className="rassd-why__item">
                <div className="rassd-why__num">{item.num}</div>
                <div>
                  <div className="rassd-why__item-title">{item.title}</div>
                  <div className="rassd-why__item-desc">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact-us" className="rassd-contact">
        <div className="rassd-contact__inner">
          <h2 className="rassd-contact__title">{t.contact.title}</h2>
          <p className="rassd-contact__subtitle">{t.contact.subtitle}</p>
          <ContactForm t={t.form} />
        </div>
      </section>

      <footer className="rassd-footer">
        <p className="rassd-footer__text">{t.footer}</p>
      </footer>

    </div>
  </>
}
